import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOffIcon from "@mui/icons-material/FolderOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import { secondAxios } from "../../../../../api/axios";
import useModuleAccess from "../../../../../hooks/useModuleAccess";
import {
  APP_MODULES,
  ALL_ESTIMATION_MODULE_NAMES,
  PLANT_MODULE_LICENCE_MAP,
} from "../../../../../constants/appModules";
import { PLANT_MODULES } from "../../../../../constants/plantModules";
import { getPersonLabel } from "../plantUtils";
import SubProjectsCrudModal from "../SubProjectsCrudModal";

export default function ProjectHub() {
  const { plantId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEstimation = searchParams.get("mode") === "estimation";

  const { hasModule, hasAnyModule } = useModuleAccess();

  const [plant, setPlant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modulesModalOpen, setModulesModalOpen] = useState(false);
  const [aimsSending, setAimsSending] = useState(false);

  const fetchPlant = async () => {
    try {
      setLoading(true);
      const res = await secondAxios.get(`/plant/${plantId}`);
      setPlant(res.data);
    } catch (err) {
      console.error("Error fetching project:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (plantId) fetchPlant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantId]);

  const handleSendToAIMS = async () => {
    if (!plantId || aimsSending) return;
    try {
      setAimsSending(true);
      await secondAxios.put(`/plant/${plantId}/aims-sent`);
      setPlant((prev) => (prev ? { ...prev, aimsSent: true } : prev));
    } catch (err) {
      console.error("Error sending project to AIMS:", err);
    } finally {
      setAimsSending(false);
    }
  };

  const visibleModules = useMemo(() => {
    const hasEstimationAccess = hasAnyModule(ALL_ESTIMATION_MODULE_NAMES);

    if (isEstimation) {
      return hasEstimationAccess
        ? PLANT_MODULES.filter((module) => module.key === "estimation")
        : [];
    }

    return PLANT_MODULES.filter((module) => {
      if (module.key === "estimation" || module.key === "docRegister")
        return false;
      return hasModule(
        PLANT_MODULE_LICENCE_MAP[module.key] ?? APP_MODULES.EPCM,
      );
    });
  }, [hasModule, hasAnyModule, isEstimation]);

  const docRegisterModule = PLANT_MODULES.find(
    (module) => module.key === "docRegister",
  );
  const hasDocRegisterAccess =
    !isEstimation &&
    Boolean(docRegisterModule) &&
    hasModule(PLANT_MODULE_LICENCE_MAP.docRegister ?? APP_MODULES.EMS);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!plant) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="text.secondary">Project not found.</Typography>
      </Box>
    );
  }

  const targetLabel = isEstimation ? "EPCM" : "AIMS";
  const isAimsSent = Boolean(plant.aimsSent);
  const people = Array.isArray(plant.people) ? plant.people : [];

  const subProjects = plant.projects || plant.project;
  const subProjectsArray = Array.isArray(subProjects)
    ? subProjects
    : subProjects
      ? [subProjects]
      : [];
  const clientEnvName = process.env.REACT_APP_CLIENT_NAME;
  const tagCount = subProjectsArray.filter(
    (p) => p && p.clientName === clientEnvName,
  ).length;

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Back to Projects
      </Button>

      <Card raised sx={{ mb: 4 }}>
        <CardContent>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ md: "flex-start" }}
            spacing={2}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {plant.name}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                {plant.description || "No description"}
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                sx={{ mt: 1.5 }}
                flexWrap="wrap"
                useFlexGap
              >
                {(plant.client?.name || plant.client?.prefix) && (
                  <Chip
                    size="small"
                    label={plant.client?.name || plant.client?.prefix}
                  />
                )}
                <Chip
                  size="small"
                  label={
                    aimsSending
                      ? "Sending..."
                      : isAimsSent
                        ? `Sent To ${targetLabel}`
                        : `Send To ${targetLabel}`
                  }
                  icon={isAimsSent ? <CheckCircleIcon /> : <AddIcon />}
                  variant={isAimsSent ? "filled" : "outlined"}
                  color={isAimsSent ? "success" : "primary"}
                  disabled={isAimsSent || aimsSending}
                  onClick={handleSendToAIMS}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  color="primary"
                  clickable
                  label={`${tagCount} WBS`}
                  onClick={() => setModulesModalOpen(true)}
                />
              </Stack>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              {hasDocRegisterAccess && (
                <Tooltip title="Document Register">
                  <Box
                    onClick={() =>
                      navigate(`/plant/${plantId}/${docRegisterModule.path}`)
                    }
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      color: "primary.main",
                      transition: "box-shadow 0.15s ease, background-color 0.15s ease",
                      "&:hover": {
                        backgroundColor: "action.hover",
                        boxShadow: 1,
                      },
                    }}
                  >
                    <docRegisterModule.icon fontSize="small" />
                    <Typography
                      variant="caption"
                      sx={{ mt: 0.25, fontWeight: 600, whiteSpace: "nowrap" }}
                    >
                      Doc Register
                    </Typography>
                  </Box>
                </Tooltip>
              )}

              {plant.sharepointLink ? (
                <Tooltip title="Open SharePoint directory">
                  <IconButton
                    component="a"
                    href={plant.sharepointLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="primary"
                  >
                    <FolderIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="No directory link">
                  <span>
                    <IconButton disabled>
                      <FolderOffIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Stack>
          </Stack>

          {people.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Project Manager & Team
              </Typography>
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 0.5 }}
              >
                {people.map((person, index) => (
                  <Chip
                    key={
                      typeof person === "object"
                        ? person.id || person.email || index
                        : person
                    }
                    size="small"
                    variant="outlined"
                    label={getPersonLabel(person)}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Modules
      </Typography>

      {visibleModules.length === 0 ? (
        <Typography color="text.secondary">
          No modules are available for your licence.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {visibleModules.map(({ key, label, path, icon: Icon }) => (
            <Grid key={key} item xs={6} sm={4} md={3} lg={2}>
              <Card
                raised
                sx={{
                  boxShadow: "0px 6px 5px rgba(182, 188, 218, 0.2)",
                  transition: "box-shadow 0.15s ease, transform 0.15s ease",
                  "&:hover": {
                    boxShadow: "0px 8px 16px rgba(182, 188, 218, 0.55)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardActionArea
                  onClick={() => navigate(`/plant/${plantId}/${path}`)}
                  sx={{
                    py: 3,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Icon color="primary" sx={{ fontSize: 36 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {label}
                  </Typography>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <SubProjectsCrudModal
        open={modulesModalOpen}
        plant={plant}
        onClose={() => setModulesModalOpen(false)}
        onProjectsChange={fetchPlant}
      />
    </Box>
  );
}
